/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect } from 'react';
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';

export interface AwsWafIntegrationApi {
    getToken: () => Promise<string>;
    hasToken?: () => boolean;
    fetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

declare global {
    interface Window {
        AwsWafIntegration?: AwsWafIntegrationApi;
    }
}

const WAF_SCRIPT_ATTR = 'data-waf-challenge';

/**
 * Injects the AWS WAF challenge script into the document head (idempotent).
 * Safe to call multiple times — subsequent calls are no-ops if the script tag
 * already exists.
 */
export function injectWafChallengeScript(scriptUrl: string): void {
    if (!scriptUrl) {
        return;
    }
    if (document.head.querySelector(`script[${WAF_SCRIPT_ATTR}="true"]`)) {
        return;
    }
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.defer = true;
    script.setAttribute(WAF_SCRIPT_ATTR, 'true');
    document.head.appendChild(script);
}

/**
 * Returns the current AWS WAF token, or null if the integration is not loaded
 * or the token cannot be retrieved. Never rejects — callers fail open (send
 * the request without the token and let WAF decide whether to challenge it).
 */
export async function getWafToken(): Promise<string | null> {
    const integration = window.AwsWafIntegration;
    if (!integration?.getToken) {
        return null;
    }
    try {
        const token = await integration.getToken();
        return token || null;
    } catch {
        return null;
    }
}

/**
 * React hook: injects the WAF challenge script when `enabled` is true.
 * Use in any component whose payment flow needs WAF token support.
 */
export function useWafChallenge(enabled: boolean): void {
    useEffect(() => {
        if (enabled) {
            injectWafChallengeScript(process.env.PRS_WAF_INTEGRATION_URL as string);
        }
    }, [enabled]);
}

/**
 * Posts a PRS payment request with WAF token (if enabled) and guards against
 * HTTP 202 (WAF silent challenge). Throws on 202 so callers treat a challenged
 * request as a failure rather than a success.
 */
export async function postPrsPayment(
    url: string,
    data: unknown,
    options: { enableWaf: boolean; headers?: Record<string, string> }
): Promise<AxiosResponse> {
    const headers: Record<string, string> = { ...options.headers };
    if (options.enableWaf) {
        const token = await getWafToken();
        if (token) {
            headers['x-aws-waf-token'] = token;
        }
    }

    const config: AxiosRequestConfig = { headers };
    const response = await axios.post(url, data, config);

    if (response.status === 202) {
        throw new Error('PRS request was challenged by WAF (202)');
    }

    return response;
}
